import { useEffect, useState, type FC } from "react";
import { Routes, Route } from "react-router";
// import { BrowserRouter } from "react-router-dom";
import { HashRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AuthProvider } from "./context/AuthContext"; // Import your AuthProvider
import ProtectedRoute from "./components/protectedRoute";
import toast, { Toaster } from "react-hot-toast";

// Import pages
import DashboardPage from "./pages";
import ForgotPasswordPage from "./pages/authentication/forgot-password";
import ProfileLockPage from "./pages/authentication/profile-lock";
import ResetPasswordPage from "./pages/authentication/reset-password";
import SignInPage from "./pages/authentication/sign-in";
import SignUpPage from "./pages/authentication/sign-up";
import EcommerceBillingPage from "./pages/e-commerce/billing";
import EcommerceInvoicePage from "./pages/e-commerce/invoice";
import EcommerceProductsPage from "./pages/e-commerce/products";
import KanbanPage from "./pages/kanban";
import MailingComposePage from "./pages/mailing/compose";
import MailingInboxPage from "./pages/mailing/inbox";
import MailingReadPage from "./pages/mailing/read";
import MailingReplyPage from "./pages/mailing/reply";
import NotFoundPage from "./pages/404";
import ServerErrorPage from "./pages/500";
import MaintenancePage from "./pages/maintenance";
import ContactFormPage from "./pages/contact-form";
import MemberFeedPage from "./pages/members/feed";
import FlowbiteWrapper from "./components/flowbite-wrapper";
import ContactsPage from "./pages/contacts";
import CompaniesPage from "./pages/companies";
import DealsPage from "./pages/deals";
import { apiWithAuth, userService } from "./services/main";
import ErrorLogger from "./utils/errorLogger";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Label,
  Table,
  TextInput,
  useTheme,
} from "flowbite-react";
import UserSettingsPage from "./pages/members/settings";
import CampaignsPage from "./pages/campaigns";
import CreateCampaignPage from "./pages/createCampaign";
import CampaignDetailPage from "./pages/campaignDetail";
import TelephonyDevicesPage from "./pages/telephonyDevices";
import WhatsAppDevicesPage from "./pages/whatsappDevices";
import UsersPage from "./pages/users";

const App: FC = function () {
  const [sessionExpired, setSessionExpired] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    ownerName: "",
    mobileNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [user, setUser] = useState(() => JSON.parse(sessionStorage.getItem("user") || "{}"));
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  const excludedPaths = [
    "/authentication/sign-up",
    "/authentication/sign-in",
    "/auth",
    "/pages/gym-profile/:userId",
  ]; // Authentication routes

  const isExcludedPath = (path: string) => {
    return excludedPaths.some((excludedPath) => {
      if (excludedPath.includes(":")) {
        const regex = new RegExp(`^${excludedPath.replace(/:\w+/g, "[^/]+")}$`);
        return regex.test(path);
      }
      return path === excludedPath;
    });
  };

  useEffect(() => {
    const checkPathChange = () => {
      const newPath = window.location.pathname;
      if (newPath !== currentPath) {
        setCurrentPath(newPath);
      }
    };

    const interval = setInterval(checkPathChange, 500); // Poll every 500ms

    return () => clearInterval(interval);
  }, [currentPath]);

  useEffect(() => {
    if (!isExcludedPath(currentPath)) {
      if (user && Object.keys(user).length > 0) {
        if (user?.mobileNumber && user?.firstName) {
          setShowInfoModal(false);
        } else {
          setShowInfoModal(true);
        }
      }
    } else {
      setShowInfoModal(false); // Ensure modal does not show on excluded paths
    }
  }, [user, currentPath]);

  useEffect(() => {
    const interval = setInterval(() => {
      const updatedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
      if (JSON.stringify(updatedUser) !== JSON.stringify(user)) {
        setUser(updatedUser);
      }
    }, 500); // Poll every 500ms for changes in sessionStorage

    return () => clearInterval(interval);
  }, [user]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { firstName, mobileNumber } = formData;

    if (!firstName || !mobileNumber) {
      setError("Please fill out all fields.");
      return;
    }

    if (!/^\d{10}$/.test(mobileNumber)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      const userDetails = { firstName, mobileNumber };

      const response = await userService.updateUser(user?._id, userDetails);
      sessionStorage.setItem("user", JSON.stringify(response.data));
      // Show success toast
      toast.success("Account updated successfully!");
      setShowInfoModal(false);
    } catch (err: any) {
      ErrorLogger.logAppError(err, 'App.tsx - handleSubmit', {
        action: 'updateUser',
        userId: user?._id,
        userDetails: { firstName, mobileNumber },
      });
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Interceptor for response with session expiration handling
  // Note: This is a duplicate interceptor - the main one is in utils.ts
  // Keeping this for App-specific session expiration handling
  apiWithAuth.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      // ErrorLogger will handle detailed logging in utils.ts
      // This interceptor is specifically for session expiration UI handling
      if (error.response && error.response.status === 403) {
        const errorMessage = error.response.data.message || "Unauthorized";
        if (
          errorMessage === "Invalid token" ||
          errorMessage === "Token expired" ||
          errorMessage === "Unauthorized"
        ) {
          ErrorLogger.logWarning("Session expired in App.tsx - setting session expired state", {
            errorMessage,
          });
          sessionStorage.removeItem("token");
          setSessionExpired(true);
        }
      }
      return Promise.reject(error);
    }
  );
  return (
    <AuthProvider>
      <HashRouter>
        <Toaster />
        <ToastContainer />
        {showInfoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <Card
              horizontal
              imgAlt=""
              className="w-full max-w-[90%] md:max-w-[700px] md:[&>*]:w-full md:[&>*]:p-16 [&>img]:hidden md:[&>img]:w-96 md:[&>img]:p-0 lg:[&>img]:block"
            >
              <h1 className="mb-3 text-2xl font-bold dark:text-white md:text-2xl">
                Please Provide the Required Information to Continue
              </h1>
              <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              >
                {[
                  { field: "firstName", placeholder: "Enter Your Gym Name" },
                  {
                    field: "mobileNumber",
                    placeholder: "Enter a 10-Digit Mobile Number",
                  },
                ].map(({ field, placeholder }) => (
                  <div key={field} className="flex flex-col gap-y-3">
                    <Label htmlFor={field}>
                      {field
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </Label>
                    <TextInput
                      id={field}
                      name={field}
                      placeholder={placeholder}
                      type={
                        field.includes("password")
                          ? "password"
                          : field === "email"
                            ? "email"
                            : "text"
                      }
                      value={formData[field as keyof typeof formData] as string}
                      onChange={handleChange}
                      required
                    />
                  </div>
                ))}

                <hr className="border-gray-300 w-auto sm:col-span-2" />
                {error && (
                  <div className="text-red-500 text-sm sm:col-span-2">
                    {error}
                  </div>
                )}
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    className="w-full lg:w-auto  text-white bg-blue-700 hover:bg-blue-800  dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800 "
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}
        <Routes>
          <Route element={<FlowbiteWrapper />}>
            {/* Public Routes */}
            <Route
              path="/authentication/sign-in"
              element={
                <ProtectedRoute publicRoute>
                  <SignInPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/authentication/sign-up"
              element={
                <ProtectedRoute publicRoute>
                  <SignUpPage />
                </ProtectedRoute>
              }
            />
            <Route path="/authentication/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/authentication/reset-password" element={<ResetPasswordPage />} />
            <Route path="/authentication/profile-lock" element={<ProfileLockPage />} />
            <Route path="/pages/404" element={<NotFoundPage />} />
            <Route path="/pages/500" element={<ServerErrorPage />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>

              <Route>
                <Route path="/" element={<DashboardPage />} index />
                <Route path="/mailing/compose" element={<MailingComposePage />} />
                <Route path="/mailing/inbox" element={<MailingInboxPage />} />
                <Route path="/mailing/read" element={<MailingReadPage />} />
                <Route path="/mailing/reply" element={<MailingReplyPage />} />
                <Route path="/kanban" element={<KanbanPage />} />
                <Route path="/pages/contacts" element={<ContactsPage />} />
                <Route path="/pages/members/:memberId" element={<MemberFeedPage />} />
                <Route path="/pages/companies" element={<CompaniesPage />} />
                <Route path="/pages/deals" element={<DealsPage />} />
                <Route path="/pages/campaigns" element={<CampaignsPage />} />
                <Route path="/pages/campaigns/new" element={<CreateCampaignPage />} />
                <Route path="/pages/campaigns/:id" element={<CampaignDetailPage />} />
                <Route path="/pages/telephony-devices" element={<TelephonyDevicesPage />} />
                <Route path="/pages/whatsapp-devices" element={<WhatsAppDevicesPage />} />
                <Route path="/pages/users" element={<UsersPage />} />
                <Route path="/pages/settings" element={<UserSettingsPage />} />
                <Route path="/pages/contact-form" element={<ContactFormPage />} />
              </Route>
            </Route>

            {/* Wildcard Route to redirect to 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </HashRouter>
    </AuthProvider>
  );
};

export default App;
